// Role-based visibility for subscription plans (admin web).
//
// Each plan from `GET /subscription-plans` carries an `allowed_role_ids`
// array. The admin tooling promises "Leave empty to make this plan visible
// to everyone" — so we treat the field as the audience grant:
//   * empty / missing → visible to any viewer
//   * non-empty       → visible only to viewers whose role id is in the list
// Superadmin (role id 1) bypasses, mirroring the `userHasModuleAccess` rule
// in `src/routes/routeModuleMap.ts`.
//
// Apply at plan-picker sites only (createModal, upgradeModal). DO NOT apply
// to the subscription-plan management screen — admins must still see every
// plan there to edit them.

export const SUPERADMIN_ROLE_ID = 1;
export const EPAPER_AGENT_ROLE_ID = 5;
export const EPAPER_ADMIN_ROLE_ID = 6;

// Roles that may always assign the complimentary (price 0) tiers, no matter how
// a plan's `allowed_role_ids` audience is scoped. Epaper agents AND epaper
// admins both register subscribers, so both need the free plans.
const FREE_PLAN_VIEWER_ROLE_IDS = [EPAPER_AGENT_ROLE_ID, EPAPER_ADMIN_ROLE_ID];

export type PlanWithAllowedRoles = {
  allowed_role_ids?: number[] | null;
  price?: number | string | null;
};

// A plan counts as "free" when its price is zero (or unset). Free plans are
// the agent-facing complimentary tiers (e.g. Management / Media Team), which
// epaper agents must be able to assign even when `allowed_role_ids` is scoped
// to internal roles.
const isFreePlan = (plan: PlanWithAllowedRoles): boolean =>
  (Number(plan.price) || 0) === 0;

// Map well-known role names back to their numeric id, for the rare case the
// auth payload arrives as a bare string instead of `{ id, name }`.
const ROLE_NAME_TO_ID: Record<string, number> = {
  superadmin: 1,
  admin: 4,
  agent: 5,
  epaper: 5, // users-permissions role *type* for Epaper Agent
  epaper_agent: 5,
  epaper_admin: 6,
  e_paper_admin: 6,
};

const normalizeRoleId = (role: unknown): number | null => {
  if (role == null) return null;
  if (typeof role === "number") return Number.isFinite(role) ? role : null;
  if (typeof role === "string") {
    const parsed = parseInt(role, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }
  if (typeof role === "object" && role && "id" in (role as any)) {
    const raw = (role as any).id;
    const parsed = typeof raw === "number" ? raw : parseInt(String(raw), 10);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const normalizeRoleName = (role: unknown): string => {
  if (role == null) return "";
  if (typeof role === "string") return role.toLowerCase().replace(/\s+/g, "_");
  if (typeof role === "object" && role && "name" in (role as any)) {
    const name = (role as any).name;
    return typeof name === "string"
      ? name.toLowerCase().replace(/\s+/g, "_")
      : "";
  }
  return "";
};

export const getUserRoleId = (user: any): number | null => {
  if (!user) return null;
  const role = user.role;
  const id = normalizeRoleId(role);
  if (id !== null) return id;
  const name = normalizeRoleName(role);
  return name in ROLE_NAME_TO_ID ? ROLE_NAME_TO_ID[name] : null;
};

export const isPlanVisibleToViewer = (
  plan: PlanWithAllowedRoles,
  viewerRoleId: number | null
): boolean => {
  if (viewerRoleId === SUPERADMIN_ROLE_ID) return true;
  // Epaper agents (5) and epaper admins (6) always see free plans, regardless
  // of how the plan's `allowed_role_ids` audience is scoped.
  if (
    viewerRoleId != null &&
    FREE_PLAN_VIEWER_ROLE_IDS.includes(viewerRoleId) &&
    isFreePlan(plan)
  )
    return true;
  const allowed = plan.allowed_role_ids;
  if (!allowed || allowed.length === 0) return true;
  if (viewerRoleId == null) return false;
  return allowed.includes(viewerRoleId);
};

export const filterPlansForViewer = <T extends PlanWithAllowedRoles>(
  plans: T[],
  viewerRoleId: number | null
): T[] => plans.filter((plan) => isPlanVisibleToViewer(plan, viewerRoleId));
